import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const LogoUploadSection = ({ horizontalLogo, verticalLogo, onLogoUpload }) => {
  const [uploading, setUploading] = useState({ horizontal: false, vertical: false });
  const [error, setError] = useState(null);

  const handleLogoUpload = async (type, event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    // Validate SVG file type
    if (file?.type !== 'image/svg+xml') {
      setError('Please upload an SVG file');
      return;
    }

    // Validate file size (2MB max)
    if (file?.size > 2097152) {
      setError('SVG file must be less than 2MB');
      return;
    }

    setUploading({ ...uploading, [type]: true });
    setError(null);

    try {
      const filePath = `${type}-logo-${Date.now()}.svg`;

      // Delete old logo if exists
      const oldLogoPath = type === 'horizontal' ? horizontalLogo : verticalLogo;
      if (oldLogoPath) {
        await supabase?.storage?.from('site-logos')?.remove([oldLogoPath]);
      }

      // Upload new logo
      const { error: uploadError } = await supabase?.storage?.from('site-logos')?.upload(filePath, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase?.storage?.from('site-logos')?.getPublicUrl(filePath);

      // Update site_settings table
      const updateField = type === 'horizontal' ? 'horizontal_logo_url' : 'vertical_logo_url';
      const { error: updateError } = await supabase?.from('site_settings')?.update({ [updateField]: urlData?.publicUrl })?.eq('id', (await supabase?.from('site_settings')?.select('id')?.single())?.data?.id);

      if (updateError) throw updateError;

      onLogoUpload(type, urlData?.publicUrl);
    } catch (error) {
      console.error('Logo upload error:', error);
      setError(`Upload failed: ${error?.message}`);
    } finally {
      setUploading({ ...uploading, [type]: false });
      event.target.value = '';
    }
  };

  return (
    <div className="modern-card">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-purple-100 rounded-lg">
          <Icon name="Image" size={24} className="text-purple-600" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-foreground">Logo Management</h2>
          <p className="text-sm text-muted-foreground">
            Upload SVG logos for horizontal and vertical layouts
          </p>
        </div>
      </div>
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
          <Icon name="AlertCircle" size={18} className="text-red-600" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Horizontal Logo */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Horizontal Logo
            </label>
            <p className="text-xs text-muted-foreground mb-3">
              Used in navigation bar (recommended: 200×50px)
            </p>
          </div>

          {horizontalLogo && (
            <div className="bg-muted/30 rounded-lg p-6 flex items-center justify-center min-h-[120px]">
              <img
                src={horizontalLogo}
                alt="Horizontal logo preview"
                className="max-h-16 w-auto"
              />
            </div>
          )}

          <label className="btn-secondary inline-flex items-center gap-2 cursor-pointer w-full justify-center">
            <Icon name="Upload" size={18} />
            {uploading?.horizontal ? 'Uploading...' : 'Upload Horizontal Logo'}
            <input
              type="file"
              accept="image/svg+xml"
              onChange={(e) => handleLogoUpload('horizontal', e)}
              disabled={uploading?.horizontal}
              className="hidden"
            />
          </label>
        </div>

        {/* Vertical Logo */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Vertical Logo
            </label>
            <p className="text-xs text-muted-foreground mb-3">
              Used in mobile menu (recommended: 100×100px)
            </p>
          </div>

          {verticalLogo && (
            <div className="bg-muted/30 rounded-lg p-6 flex items-center justify-center min-h-[120px]">
              <img
                src={verticalLogo}
                alt="Vertical logo preview"
                className="max-h-24 w-auto"
              />
            </div>
          )}

          <label className="btn-secondary inline-flex items-center gap-2 cursor-pointer w-full justify-center">
            <Icon name="Upload" size={18} />
            {uploading?.vertical ? 'Uploading...' : 'Upload Vertical Logo'}
            <input
              type="file"
              accept="image/svg+xml"
              onChange={(e) => handleLogoUpload('vertical', e)}
              disabled={uploading?.vertical}
              className="hidden"
            />
          </label>
        </div>
      </div>
      {/* Instructions */}
      <div className="mt-6 p-4 bg-accent/10 border border-accent/20 rounded-lg">
        <div className="flex items-start gap-2">
          <Icon name="Info" size={18} className="text-accent flex-shrink-0 mt-0.5" />
          <div className="text-sm text-muted-foreground">
            <p className="font-medium text-foreground mb-1">SVG Requirements:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>File format: SVG only (scalable vector graphics)</li>
              <li>Maximum file size: 2MB</li>
              <li>Ensure SVG has transparent background if needed</li>
              <li>Logos are publicly accessible and cached for performance</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LogoUploadSection;